import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
const VerificationTemplate = lazy(
  () => import("../../shared/app/VerificationTemplate")
);
import { DeleteTemplateModal } from "../../shared/app/VerificationTemplate/modals/DeleteTemplateModal";
import { CreateTemplateModal } from "../../shared/app/VerificationTemplate/modals/CreateTemplateModal";
import { Layout } from "providers/Layout";
import { useVerificationTemplateController } from "./controller";
import {
  Template,
  ApplicantSettingsErrors,
  VerificationSchemas
} from "types/landlord/verificationTemplate";
import { LayoutHeader } from "../../shared/app/VerificationTemplate/components/LayoutHeader";
import { Loader } from "shared/components/Loader";
import { useQuery } from "@tanstack/react-query";
import templatesApi from "api/landlord/templates";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { ApplicantType, VerificationNames } from "shared/types";
import { toast } from "react-toastify";
import { useVerificationTemplatesController } from "../verification-templates/controller";
import { SideBar } from "../../shared/app/VerificationTemplate/components/SideBar";
import { LayoutFooter } from "../../shared/app/VerificationTemplate/components/LayoutFooter";
import { onSaveValidation } from "../../shared/app/VerificationTemplate/validations";
import { onSaveCleanUp } from "../../shared/app/VerificationTemplate/helpers";

export const VerificationTemplatesIdView = () => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [duplicateTemplate, setDuplicateTemplate] = useState<Template | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [applicantSettingsErrors, setApplicantSettingsErrors] =
    useState<ApplicantSettingsErrors | null>(null);

  const { defaultTemplate } = useVerificationTemplatesController();

  const {
    templateQuery,
    deleteTemplate,
    updateTemplate,
    createTemplate,
    verificationSchemas
  } = useVerificationTemplateController();

  useQuery({
    queryKey: ["DEFAULT_CRITERIA_SETTINGS"],
    queryFn: () =>
      templatesApi.getDefaultCriteria().then((defaultCriteria) => {
        if (!template?.screening_criteria)
          setTemplate((prevTemplate: Template | null) =>
            prevTemplate
              ? {
                  ...prevTemplate,
                  screening_criteria: defaultCriteria
                }
              : null
          );
      }),
    enabled: Boolean(template)
  });

  const toggleEditMode = () => {
    if (isEditing && editedName !== template?.name)
      setTemplate((prevTemplate: Template | null) =>
        prevTemplate
          ? {
              ...prevTemplate,
              name: editedName
            }
          : null
      );

    setIsEditing(!isEditing);
  };

  useEffect(() => {
    setEditedName(template?.name || "");
  }, [template?.name]);

  useEffect(() => {
    if (templateQuery.data?.verification_template) {
      const currentVerificationTemplate =
        templateQuery.data.verification_template;
      if (defaultTemplate) {
        const verification_schemas = [
          "applicant",
          "co_signer",
          "occupant"
        ].reduce(
          (acc, prev) => {
            const initialSchemas = defaultTemplate.verification_schemas[prev];

            const currentSchemas = currentVerificationTemplate
              .verification_schemas[prev]
              ? currentVerificationTemplate.verification_schemas[prev]
              : initialSchemas;

            const requiredFieldSteps = [
              "pets",
              "emergency_contacts",
              "vehicles"
            ];
            const rentalApplicationRequired = requiredFieldSteps.reduce(
              (acc, step) => {
                const key = `${step}_step_required`;
                return {
                  ...acc,
                  [key]: {
                    ...initialSchemas.rental_application[key],
                    ...currentSchemas.rental_application[key]
                  }
                };
              },
              {}
            );

            currentSchemas.rental_application = {
              ...currentSchemas.rental_application,
              ...rentalApplicationRequired
            };

            currentSchemas.income_verification = {
              ...initialSchemas.income_verification,
              ...currentSchemas.income_verification
            };

            return {
              ...acc,
              [prev]: currentSchemas
            };
          },
          { applicant: {}, co_signer: {}, occupant: {} }
        ) as VerificationSchemas;
        currentVerificationTemplate.verification_schemas = verification_schemas;
      }

      currentVerificationTemplate.settings = {
        ...currentVerificationTemplate.settings,
        applicant: {
          ...currentVerificationTemplate.settings.applicant,
          enabled_screening_services: true
        },
        co_signer: {
          ...currentVerificationTemplate.settings.co_signer,
          enabled_screening_services:
            currentVerificationTemplate.settings.co_signer
              .enabled_screening_services ?? true
        },
        occupant: {
          ...currentVerificationTemplate.settings.occupant,
          enabled_screening_services:
            currentVerificationTemplate.settings.occupant
              ?.enabled_screening_services ?? true
        }
      };

      setTemplate(currentVerificationTemplate);
    }
  }, [defaultTemplate, templateQuery.data?.verification_template]);

  const { type } = useAuthContext();

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (templateQuery.isLoading)
    return (
      <Layout>
        <Loader />
      </Layout>
    );

  if (templateQuery.isError)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Error occurred finding template</h1>
        <p className="text-base">{templateQuery.error.message}</p>
      </Layout>
    );

  if (!template || !templateQuery.data?.verification_template)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Template not found</h1>
      </Layout>
    );

  const handleCancel = () => {
    navigate(`/verification-templates`);
  };

  const handleSaveClick = () => {
    const rulesError = onSaveValidation.rules(template);
    if (rulesError) {
      toast.error(`Error in rules: ${rulesError}`);
      return;
    }

    const criteriaError = onSaveValidation.criteria(
      template,
      setApplicantSettingsErrors
    );
    if (criteriaError) {
      toast.error(`Error in criteria: ${criteriaError}`);
      return;
    }

    let updatedTemplate = onSaveCleanUp.additionalQuestions(template);

    updatedTemplate = onSaveCleanUp.identityVerificationSettings(
      updatedTemplate,
      editedName
    );

    updateTemplate(updatedTemplate);

    // Clear modals
    setShowDeleteModal(false);
    setShowDuplicateModal(false);
  };

  return (
    <>
      <div className="bg-background-900">
        <Suspense fallback={<Loader />}>
          <VerificationTemplate
            layoutProps={{
              header: <LayoutHeader />,
              sidebar: (
                <SideBar
                  handleDuplicate={() => {
                    setDuplicateTemplate({ ...template });
                    setShowDuplicateModal(true);
                  }}
                  isEditing={isEditing}
                  template={template}
                  editedName={editedName}
                  setEditedName={setEditedName}
                  toggleEditMode={toggleEditMode}
                  setShowDeleteModal={setShowDeleteModal}
                />
              ),
              footer: (
                <LayoutFooter
                  onCancel={handleCancel}
                  onSave={() => {
                    if (!template.name)
                      toast.error("Template name is required");
                    handleSaveClick();
                  }}
                />
              )
            }}
            verificationTemplate={template}
            savedTemplate={templateQuery.data.verification_template}
            verificationSchemas={verificationSchemas || []}
            onTemplateUpdate={setTemplate}
            onApplicantSettingsErrorsUpdate={setApplicantSettingsErrors}
            originalQualificationsDisclosureText={
              template.verification_schemas[ApplicantType.Applicant][
                VerificationNames.TermsAndConditions
              ]?.additional_text || ""
            }
            originalSubmissionDisclosureText={
              template.verification_schemas[ApplicantType.Applicant][
                VerificationNames.SubmissionDisclosure
              ]?.additional_text || ""
            }
            applicantSettingsErrors={applicantSettingsErrors}
          />
        </Suspense>
      </div>

      <CreateTemplateModal
        opened={showDuplicateModal}
        template={duplicateTemplate}
        verificationSchemas={verificationSchemas || []}
        handleCancel={() => setShowDuplicateModal(false)}
        handleSave={createTemplate}
        onTemplateUpdate={setDuplicateTemplate}
      />
      <DeleteTemplateModal
        opened={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={deleteTemplate}
      />
    </>
  );
};
